import { useState } from 'react'
import { motion } from 'framer-motion'
import RouteRoundedIcon from '@mui/icons-material/RouteRounded'
import { Button } from './Common/Button'
import { PREMIUM_ACTION_COSTS } from '../services/creditStorefrontService'
import { walletService } from '../services/walletService'
import { registerEphemeralTour } from './tourguide/TourRegistry'
import { logTourSanitizerAnomaly, sanitizeTourDefinitionForRuntime } from './tourguide/TourSanitizer'
import { useTourGuide } from './tourguide/useTourGuide'
import { useWorkspace } from '../context/useWorkspace'
import { ExecutionPreviewCard } from './AiExecution'
import type { AiExecutionTheaterOutline } from '../services/ai/execution/executionTheater'
import type { AiExecutionGraph, AiOperationSimulation, AiOperationalRiskLevel } from '../types/aiOperational'
import type { TourDefinition } from './tourguide/types'

const CONFIDENT_COPY = 'I have successfully mapped your request into a real-time guided walkthrough. Review the path parameters below and click Begin Journey when you are ready to proceed step-by-step.'
const APOLOGY_COPY = 'Sorry I will not be able to do that for you but guide you step by step.'
const PURPOSE = 'TOUR_CONCIERGE_EXECUTE'
const COST = PREMIUM_ACTION_COSTS.TOUR_GENERATION

export interface JourneyTriggerCardProps {
  /** Turn id this card lives under. Surfaced as data-journey-card-id so the
   * affirmation regex interceptor can fire Begin Journey via a synthetic click. */
  turnId: string
  workspaceId: string
  tour: TourDefinition
  preambleKind: 'CONFIDENT' | 'APOLOGY'
  executionPreview?: string
  executionTheater?: AiExecutionTheaterOutline | null
  executionGraph?: AiExecutionGraph | null
  executionSimulation?: AiOperationSimulation | null
  /** True once the 15-credit Begin Journey burn cleared for this turn. */
  purchased: boolean
  /** Called after a successful commitCredits so the parent can mark the turn `purchased`. */
  onPurchased: () => void
  /** Surface error copy on the card without throwing. */
  onError: (message: string) => void
}

export const JourneyTriggerCard = ({
  turnId,
  workspaceId,
  tour,
  preambleKind,
  executionPreview,
  executionTheater,
  executionGraph,
  executionSimulation,
  purchased,
  onPurchased,
  onError,
}: JourneyTriggerCardProps) => {
  const { startTour } = useTourGuide()
  const { canPerform } = useWorkspace()
  const [busy, setBusy] = useState(false)

  const executionRisk: AiOperationalRiskLevel = executionTheater?.riskLabel.includes('restricted')
    ? 'RESTRICTED'
    : executionTheater?.riskLabel.includes('high')
      ? 'HIGH'
      : executionTheater?.riskLabel.includes('medium')
        ? 'MEDIUM'
        : 'LOW'

  const playTour = (): boolean => {
    try {
      const sanitized = sanitizeTourDefinitionForRuntime(tour, { canPerform })
      registerEphemeralTour(sanitized)
      return startTour(sanitized.id, { force: true })
    } catch (error) {
      void logTourSanitizerAnomaly({
        workspaceId,
        tourId: tour.id,
        reason: error instanceof Error ? error.message : 'Client-side tour sanitizer rejected the stored tour.',
        source: 'JOURNEY_TRIGGER_REPLAY',
      }).catch(() => undefined)
      onError(error instanceof Error ? error.message : 'The generated tour did not pass the safety checks.')
      return false
    }
  }

  const beginJourney = async () => {
    if (busy) return
    if (purchased) {
      if (!playTour()) onError('Could not start the tour — close any active tour first.')
      return
    }
    setBusy(true)
    let reservationId: string | null = null
    try {
      const reservation = await walletService.reserveCredits(workspaceId, COST, { purpose: PURPOSE })
      reservationId = reservation.reservationId
      const started = playTour()
      if (!started) {
        await walletService.releaseCredits(workspaceId, reservationId)
        onError('Could not start the tour — close any active tour first.')
        return
      }
      await walletService.commitCredits(workspaceId, reservationId, COST)
      onPurchased()
    } catch (error) {
      if (reservationId) {
        try { await walletService.releaseCredits(workspaceId, reservationId) } catch {/* refund best-effort */}
      }
      onError(error instanceof Error ? error.message : 'Could not charge for this tour.')
    } finally {
      setBusy(false)
    }
  }

  return (
    <motion.div
      className="journeyTriggerCard"
      data-preamble={preambleKind}
      data-journey-card-id={turnId}
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
    >
      <div className="journeyTriggerCardHead">
        <span className="journeyTriggerCardIcon" aria-hidden="true"><RouteRoundedIcon fontSize="small" /></span>
        <p className="journeyTriggerCardPreamble">
          {preambleKind === 'APOLOGY' ? APOLOGY_COPY : CONFIDENT_COPY}
        </p>
      </div>
      {tour.title && (
        <p className="journeyTriggerCardTitle">{tour.title}</p>
      )}
      {tour.description && (
        <p className="journeyTriggerCardDescription">{tour.description}</p>
      )}
      {(executionPreview || executionTheater) && (
        <ExecutionPreviewCard
          className="journeyTriggerCardPreview"
          title="Guided execution preview"
          summary={executionPreview ?? executionTheater?.summary ?? 'Line prepared this as a guided workspace operation.'}
          risk={executionRisk}
          authority="CLIENT_LOCAL"
          phase="PREVIEWING"
          reviewLabel={executionRisk === 'LOW' ? 'Guidance only' : 'Review preparation only'}
          rollback={null}
          affectedRecords={[]}
          executionGraph={executionGraph}
          simulation={executionSimulation}
        />
      )}
      {executionTheater && (
        <div className="journeyTriggerCardTheater" aria-label="Operational execution path">
          <div className="journeyTriggerCardTheaterHeader">
            <span>{executionTheater.riskLabel}</span>
            {executionTheater.graphStateLabel ? <span>{executionTheater.graphStateLabel}</span> : null}
            <span>{executionTheater.autoStartLabel}</span>
          </div>
          <p className="journeyTriggerCardTheaterSummary">{executionTheater.summary}</p>
          <p className="journeyTriggerCardTheaterCheckpoint">{executionTheater.checkpointLabel}</p>
          {executionTheater.checkpointSummaries.length ? (
            <ul className="journeyTriggerCardTheaterCheckpoints">
              {executionTheater.checkpointSummaries.map((checkpoint) => <li key={checkpoint}>{checkpoint}</li>)}
            </ul>
          ) : null}
          <ol className="journeyTriggerCardTheaterCues">
            {executionTheater.cues.slice(0, 4).map((cue) => (
              <li key={cue.id}>
                <span className="journeyTriggerCardTheaterCueTitle">{cue.title}</span>
                <span className="journeyTriggerCardTheaterCueMeta">{cue.authorityLabel}{cue.requiresUserAction ? ' • user action' : ''}</span>
              </li>
            ))}
          </ol>
        </div>
      )}
      <ul className="journeyTriggerCardSteps">
        {tour.steps.slice(0, 5).map((step, index) => (
          <li key={step.id}>
            <span className="journeyTriggerCardStepIndex">{index + 1}</span>
            <span className="journeyTriggerCardStepTitle">{step.title}</span>
          </li>
        ))}
        {tour.steps.length > 5 && (
          <li className="journeyTriggerCardStepsOverflow">+{tour.steps.length - 5} more step{tour.steps.length - 5 === 1 ? '' : 's'}</li>
        )}
      </ul>
      <div className="journeyTriggerCardFooter">
        <div className="journeyTriggerCardCost">
          <span className="journeyTriggerCardCostLabel">{purchased ? 'Replay' : 'Cost'}</span>
          <span className="journeyTriggerCardCostValue">{purchased ? 'Free' : `${COST} credits`}</span>
        </div>
        <Button
          variant="primary"
          size="md"
          className="journeyTriggerCardButton"
          onClick={() => void beginJourney()}
          disabled={busy}
        >
          {busy ? 'Verifying wallet…' : purchased ? 'Replay Tour' : 'Begin Journey'}
        </Button>
      </div>
    </motion.div>
  )
}
